import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, useParams, useRoutes } from 'react-router-dom';

import { useCabinet } from '@app/shared/lib/cabinet/CabinetProvider';

import { getLocationReferral } from '@app/shared/lib/get-location-referral';
import { useNavigationHistory, useSchool } from '@app/shared/lib/hooks';
import { useUser } from '@app/shared/lib/hooks/useUser';
import { LoadingOverlay } from '@app/shared/ui';
import { BasicLayout } from '@app/shared/ui/BasicLayout/BasicLayout';
import { LearnLayout } from '@app/shared/ui/LearnLayout';
import { AxlDataProvider } from '@app/shared/lib/AxlDataProvider';
import { CabinetPageViewer } from './cabinet';

const CabinetOrAuthLayout = lazy(() => import('./auth').then(m => ({ default: m.CabinetOrAuthLayout })));
const Login = lazy(() => import('./login'));
const LoginToken = lazy(() => import('./login-token'));
const LoginAs = lazy(() => import('./login-as'));
const SchoolMaterialsPage = lazy(() => import('./school-materials'));
const AccessPage = lazy(() => import('./access'));
const PartnershipPage = lazy(() => import('./partnership'));
const BillingPage = lazy(() => import('./billing'));
const RegisterPage = lazy(() => import('./register'));
const AuthContactPage = lazy(() => import('./auth-contact'));
const ForgotPassword = lazy(() => import('./forgot-password'));
const ResetPassword = lazy(() => import('./reset-password-token'));
const ConfirmEmail = lazy(() => import('./confirm-email'));
const ConfirmInvite = lazy(() => import('./confirm-invite'));
const UnsubscribeEmails = lazy(() => import('./email-unsubscribe'));
const UnsubscribeEmailNotifications = lazy(() => import('./email-notification-unsubscribe'));
const LearnPage = lazy(() => import('./learn'));
const PreviewPage = lazy(() => import('./preview'));
const StudentPreviewPage = lazy(() => import('./student-preview'));
const GamificationPage = lazy(() => import('./gamification'));
const LearnLibraryPage = lazy(() => import('./library'));
const ForumPage = lazy(() => import('./forum'));
const GroupChatPage = lazy(() => import('./group-chat'));
// Check if a module required by a segment is disabled
const isModuleDisabled = (school: ReturnType<typeof useSchool>, segment?: string): boolean => {
    if (!segment) return false;
    if (segment === 'forum' && !school.isForumAvailable) return true;
    if (segment === 'partnership' && !school.partnership) return true;
    if (segment === 'gamification' && !school.gamification) return true;
    return false;
};

// Renders CabinetPageViewer if cabinet is enabled and has a page for the given segment,
// otherwise renders the standard page. When segment is undefined, uses the first root page.
const CabinetOrStandard: React.FC<{ segment?: string; children: React.ReactNode }> = ({ segment, children }) => {
    const school = useSchool();
    const { isLoading, getPageBySegment, rootPages } = useCabinet();
    if (!school.isCabinetEnabled) return <>{children}</>;
    if (isModuleDisabled(school, segment)) return <>{children}</>;
    if (isLoading) return <LoadingOverlay />;
    const page = segment ? getPageBySegment(segment) : rootPages[0];
    if (!page) return <>{children}</>;
    return <AxlDataProvider><CabinetPageViewer segment={segment} /></AxlDataProvider>;
};

// Handles any cabinet page by URL path segments (any depth: /seg1, /seg1/seg2, /seg1/seg2/seg3/...)
const DynamicCabinetRoute: React.FC = () => {
    const params = useParams();
    const school = useSchool();
    const { isLoading, getPageBySegments, getPageBySegment } = useCabinet();
    if (!school.isCabinetEnabled) return <Navigate to="/" />;
    if (isLoading) return <LoadingOverlay />;

    const wildcard = params['*'] ?? '';
    const segments = wildcard.split('/').filter(Boolean);

    const page = segments.length >= 2
        ? getPageBySegments(...segments)
        : segments.length === 1
            ? getPageBySegment(segments[0]!)
            : undefined;
    if (!page) return <Navigate to="/" />;
    return <AxlDataProvider><CabinetPageViewer segment={page.segment} /></AxlDataProvider>;
};

const BasicLayoutRouting: React.FC = () => {
    const user = useUser();
    const school = useSchool();

    // FIXME: надо isAuthenticated
    if (user.user === null) {
        return <Navigate to={`/login?returnUrl=${encodeURIComponent(getLocationReferral())}`} />;
    }

    // migration lock
    const hasMigrationLock = school.hasMigrationLock ?? false;
    if (hasMigrationLock) {
        if (window.location.pathname !== '/') return <Navigate to="/" />;
    } else if (school.isLocked && !window.location.pathname.startsWith('/locked')) return <Navigate to="/locked" />; // billing lock

    return (
        <Suspense fallback={<LoadingOverlay />}>
            <BasicLayout />
        </Suspense>
    );
};

const Router = () => {
    const user = useUser();
    useNavigationHistory();
    // We removed the <BrowserRouter> element from App because the
    // useRoutes hook needs to be in the context of a <BrowserRouter>
    // element. This is a common pattern with React Router apps that
    // are rendered in different environments. To render an <App>,
    // you'll need to wrap it in your own <BrowserRouter> element.
    const element = useRoutes([
        {
            path: '/',
            element: <BasicLayoutRouting />,
            children: [
                {
                    path: '/',
                    element: (
                        <CabinetOrStandard>
                            <Suspense fallback={<LoadingOverlay />}><SchoolMaterialsPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                { path: '/courses', element: <Navigate to="/" /> },
                {
                    path: '/access',
                    element: (
                        <CabinetOrStandard segment="access">
                            <Suspense fallback={<LoadingOverlay />}><AccessPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                {
                    path: '/partnership',
                    element: (
                        <CabinetOrStandard segment="partnership">
                            <Suspense fallback={<LoadingOverlay />}><PartnershipPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                {
                    path: '/gamification',
                    element: (
                        <CabinetOrStandard segment="gamification">
                            <Suspense fallback={<LoadingOverlay />}><GamificationPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                {
                    path: '/forum',
                    element: (
                        <CabinetOrStandard segment="forum">
                            <Suspense fallback={<LoadingOverlay />}><ForumPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                {
                    path: '/forum/:threadId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForumPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forum/:threadId/:topicId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForumPage />
                        </Suspense>
                    )
                },
                {
                    path: '/group-chat',
                    element: (
                        <CabinetOrStandard segment="group-chat">
                            <Suspense fallback={<LoadingOverlay />}><GroupChatPage /></Suspense>
                        </CabinetOrStandard>
                    )
                },
                {
                    path: '/locked',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <BillingPage />
                        </Suspense>
                    )
                },
                {
                    path: '/*',
                    element: <DynamicCabinetRoute />
                }
            ]
        },
        {
            path: '/',
            element: (
                <Suspense fallback={<LoadingOverlay />}>
                    <CabinetOrAuthLayout />
                </Suspense>
            ),
            children: [
                {
                    path: '/login',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <Login />
                        </Suspense>
                    )
                },
                {
                    path: '/login-token/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LoginToken />
                        </Suspense>
                    )
                },
                {
                    path: '/login-as/:sessionId/:studentId/:timestamp/:signature',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LoginAs />
                        </Suspense>
                    )
                },
                {
                    path: '/register',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <RegisterPage />
                        </Suspense>
                    )
                },
                {
                    path: '/c/:contactId/:inviteToken?',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <AuthContactPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forgot-password',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForgotPassword />
                        </Suspense>
                    )
                },
                {
                    path: '/reset-password/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ResetPassword />
                        </Suspense>
                    )
                },
                {
                    path: '/confirm-email/:userId/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ConfirmEmail />
                        </Suspense>
                    )
                },
                {
                    path: '/confirm-invite/:userId/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ConfirmInvite />
                        </Suspense>
                    )
                },
                {
                    path: '/email/unsubscribe',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <UnsubscribeEmails />
                        </Suspense>
                    )
                },
                {
                    path: '/email-notification/unsubscribe',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <UnsubscribeEmailNotifications />
                        </Suspense>
                    )
                }
            ]
        },
        {
            path: '/',
            // FIXME: надо isAuthenticated
            element:
                user.user !== null ? (
                    <Suspense fallback={<LoadingOverlay />}>
                        <LearnLayout />
                    </Suspense>
                ) : (
                    <Navigate to={`/login?returnUrl=${encodeURIComponent(getLocationReferral())}`} />
                ),
            children: [
                {
                    path: '/learn/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnPage />
                        </Suspense>
                    )
                },
                {
                    path: '/learn/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/library/:libraryId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnLibraryPage />
                        </Suspense>
                    )
                },
                {
                    path: '/library/:libraryId/:libraryItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnLibraryPage />
                        </Suspense>
                    )
                }
            ]
        },
        {
            path: '*',
            element: <Navigate to="/" />
        }
    ]);

    return element;
};

export const Pages: React.FC = () => (
    <BrowserRouter>
        <Router />
    </BrowserRouter>
);
